import React, { useEffect, useMemo, useState } from 'react';
import { CustomerOption } from '../newOrderTypes';
import { SearchInput } from '../../shared';
import { useCustomersStore } from '../../../../store/customersStore';
import { Customer } from '../../../../services/customersApi';
import styles from './CustomerStep.module.css';

interface CustomerStepProps {
  selectedId: string | null;
  setSelectedId: (id: string) => void;
}

const creditClass: Record<CustomerOption['creditStatus'], string> = {
  Good: 'creditGood',
  Excellent: 'creditExcellent',
  Fair: 'creditFair',
};

function adapt(c: Customer): CustomerOption {
  return {
    id: String(c.id),
    name: c.store_name || c.full_name,
    area: [c.city, c.state].filter(Boolean).join(', ') || '—',
    creditStatus:
      c.metrics.outstanding_balance === 0
        ? 'Excellent'
        : c.metrics.outstanding_balance < c.metrics.total_revenue * 0.3
          ? 'Good'
          : 'Fair',
  };
}

export const CustomerStep: React.FC<CustomerStepProps> = ({ selectedId, setSelectedId }) => {
  const [search, setSearch] = useState('');
  const customers = useCustomersStore((s) => s.items);
  const fetchCustomers = useCustomersStore((s) => s.fetchList);

  useEffect(() => {
    if (customers.length === 0) fetchCustomers().catch(() => undefined);
  }, [customers.length, fetchCustomers]);

  const adapted = useMemo(() => customers.map(adapt), [customers]);
  const filtered = useMemo(() => {
    if (!search.trim()) return adapted;
    const q = search.toLowerCase();
    return adapted.filter((c) => c.name.toLowerCase().includes(q));
  }, [adapted, search]);

  return (
    <div className={styles.wrap}>
      <h3 className={styles.title}>
        <span className={styles.titleIcon}>👤</span> Select Customer
      </h3>
      <p className={styles.subtitle}>Choose who you're creating this order for</p>

      <SearchInput
        placeholder="Search customers by name..."
        value={search}
        onChange={setSearch}
      />

      <div className={styles.list}>
        {filtered.map((c) => (
          <button
            key={c.id}
            className={`${styles.customer} ${selectedId === c.id ? styles.customerSelected : ''}`}
            onClick={() => setSelectedId(c.id)}
          >
            <div>
              <p className={styles.name}>
                {c.name}
                {selectedId === c.id && <span className={styles.checkIcon}>✓</span>}
              </p>
              <p className={styles.area}>{c.area}</p>
              <p className={styles.creditRow}>
                Credit Status:{' '}
                <span className={`${styles.creditBadge} ${styles[creditClass[c.creditStatus]]}`}>
                  {c.creditStatus}
                </span>
              </p>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <p style={{ color: '#6B7280', fontSize: 14, padding: 12 }}>No customers found.</p>
        )}
      </div>
    </div>
  );
};
